import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function JobApproval() {
    const { token } = useParams();

    const [place, setPlace] = useState('');
    const [orderedBy, setOrderedBy] = useState('');

    const [workerName, setWorkerName] = useState('');
    const [workDone, setWorkDone] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    
    const [images, setImages] = useState([]);
    const [signatureOrderer, setSignatureOrderer] = useState('');
    const [signatureWorker, setSignatureWorker] = useState('');

    const [status, setStatus] = useState('new');
    const [loading, setLoading] = useState(!!token);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [generatedLink, setGeneratedLink] = useState('');
    
    const canvasRef = useRef(null);
    const workerCanvasRef = useRef(null);
    const fileInputRef = useRef(null);

    // Signature drawing state refs (using refs to avoid re-renders during drawing)
    const drawingStateRef = useRef({
        isDrawing: false,
        activeCanvas: null,
        lastX: 0,
        lastY: 0,
        lastTime: 0,
        lastLineWidth: 2.5,
        points: [],
        hasDrawn: { orderer: false, worker: false }
    });

    // Fetch existing form if token is present
    useEffect(() => {
        if (token) {
            fetch(`${API_BASE}/api/jobApprovals/token/${token}`)
                .then(res => {
                    if (!res.ok) throw new Error('הטופס לא נמצא או שגיאת שרת');
                    return res.json();
                })
                .then(data => {
                    setPlace(data.place);
                    setOrderedBy(data.orderedBy);

                    setWorkerName(data.workerName);
                    setWorkDone(data.workDone);
                    setDate(new Date(data.date).toISOString().split('T')[0]);
                    setStatus(data.status);
                    setSignatureOrderer(data.signatureOrderer || '');
                    setSignatureWorker(data.signatureWorker || '');
                    if (data.images && data.images.length > 0) {
                        setImages(data.images.map(url => ({ url: `/uploads/${url}` })));
                    }
                    setLoading(false);
                })
                .catch(err => {
                    setError(err.message);
                    setLoading(false);
                });
        }
    }, [token]);

    const isReadOnly = status === 'completed';
    const isNew = status === 'new';

    // ─── Improved Signature Drawing System ────────────────────────

    const initCanvas = useCallback((canvasRefObj) => {
        const canvas = canvasRefObj.current;
        if (!canvas) return;

        // HiDPI / Retina support
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);

        // Draw guide line
        drawGuideLine(canvas, ctx);

        // Draw watermark
        const key = canvasRefObj === canvasRef ? 'orderer' : 'worker';
        if (!drawingStateRef.current.hasDrawn[key]) {
            drawWatermark(canvas, ctx);
        }
    }, []);

    const drawGuideLine = (canvas, ctx) => {
        const dpr = window.devicePixelRatio || 1;
        const w = canvas.width / dpr;
        const h = canvas.height / dpr;
        ctx.save();
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = '#cbd5e1';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(20, h * 0.72);
        ctx.lineTo(w - 20, h * 0.72);
        ctx.stroke();
        ctx.restore();
    };

    const drawWatermark = (canvas, ctx) => {
        const dpr = window.devicePixelRatio || 1;
        const w = canvas.width / dpr;
        const h = canvas.height / dpr;
        ctx.save();
        ctx.font = '14px "Segoe UI", Tahoma, sans-serif';
        ctx.fillStyle = '#cbd5e1';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('חתום כאן ✍️', w / 2, h * 0.42);
        ctx.restore();
    };

    // Initialize canvases on mount and resize
    useEffect(() => {
        const timer = setTimeout(() => {
            if (canvasRef.current) initCanvas(canvasRef);
            if (workerCanvasRef.current) initCanvas(workerCanvasRef);
        }, 100);

        const handleResize = () => {
            if (canvasRef.current) initCanvas(canvasRef);
            if (workerCanvasRef.current) initCanvas(workerCanvasRef);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', handleResize);
        };
    }, [initCanvas, status]);

    const getCanvasCoords = (canvasRefObj, e) => {
        const canvas = canvasRefObj.current;
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        return {
            x: clientX - rect.left,
            y: clientY - rect.top
        };
    };

    const startDraw = (canvasRefObj) => (e) => {
        if (isReadOnly) return;
        e.preventDefault();

        const coords = getCanvasCoords(canvasRefObj, e);
        const state = drawingStateRef.current;
        
        state.isDrawing = true;
        state.activeCanvas = canvasRefObj;
        state.lastX = coords.x;
        state.lastY = coords.y;
        state.lastTime = Date.now();
        state.lastLineWidth = 2.5;
        state.points = [coords];

        // Mark as drawn (remove watermark)
        const key = canvasRefObj === canvasRef ? 'orderer' : 'worker';
        if (!state.hasDrawn[key]) {
            state.hasDrawn[key] = true;
            // Redraw canvas without watermark
            const canvas = canvasRefObj.current;
            const ctx = canvas.getContext('2d');
            const dpr = window.devicePixelRatio || 1;
            ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
            drawGuideLine(canvas, ctx);
        }

        const canvas = canvasRefObj.current;
        const ctx = canvas.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(coords.x, coords.y);
    };

    const draw = useCallback((e) => {
        const state = drawingStateRef.current;
        if (!state.isDrawing || !state.activeCanvas || isReadOnly) return;
        e.preventDefault();

        const canvas = state.activeCanvas.current;
        const ctx = canvas.getContext('2d');
        const coords = getCanvasCoords(state.activeCanvas, e);

        // Calculate speed for pressure-like effect
        const now = Date.now();
        const dt = now - state.lastTime;
        const dx = coords.x - state.lastX;
        const dy = coords.y - state.lastY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const speed = dt > 0 ? distance / dt : 0;

        // Map speed to line width (slower = thicker, faster = thinner)
        const targetWidth = Math.max(1.5, Math.min(4, 3.5 - speed * 3));
        // Smooth the transition
        const lineWidth = state.lastLineWidth + (targetWidth - state.lastLineWidth) * 0.3;
        state.lastLineWidth = lineWidth;

        // Use quadratic curve for smooth strokes
        const midX = (state.lastX + coords.x) / 2;
        const midY = (state.lastY + coords.y) / 2;

        ctx.lineWidth = lineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#1a2744';
        ctx.globalAlpha = 0.92;

        ctx.beginPath();
        ctx.moveTo(state.lastX, state.lastY);
        ctx.quadraticCurveTo(state.lastX, state.lastY, midX, midY);
        ctx.stroke();

        state.lastX = coords.x;
        state.lastY = coords.y;
        state.lastTime = now;
        state.points.push(coords);
    }, [isReadOnly]);

    const stopDraw = useCallback(() => {
        drawingStateRef.current.isDrawing = false;
        drawingStateRef.current.activeCanvas = null;
    }, []);

    // Global mouse/touch move/up listeners for smooth drawing
    useEffect(() => {
        const handleMove = (e) => draw(e);
        const handleUp = () => stopDraw();

        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
        window.addEventListener('touchmove', handleMove, { passive: false });
        window.addEventListener('touchend', handleUp);

        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', handleUp);
        };
    }, [draw, stopDraw]);

    const clearCanvas = (canvasRefObj) => () => {
        if (isReadOnly) return;
        const canvas = canvasRefObj.current;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;

        // Smooth fade-out animation
        let alpha = 1;
        const fade = () => {
            alpha -= 0.15;
            if (alpha <= 0) {
                ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
                const key = canvasRefObj === canvasRef ? 'orderer' : 'worker';
                drawingStateRef.current.hasDrawn[key] = false;
                drawGuideLine(canvas, ctx);
                drawWatermark(canvas, ctx);
                return;
            }
            ctx.globalAlpha = alpha;
            requestAnimationFrame(fade);
        };
        fade();
    };

    const handleImageAdd = (e) => {
        if (isReadOnly) return;
        const files = Array.from(e.target.files);
        if (!files.length) return;

        const remaining = 3 - images.length;
        const toProcess = files.slice(0, remaining);

        const newImages = toProcess.map(file => ({
            file,
            preview: URL.createObjectURL(file)
        }));

        setImages(prev => [...prev, ...newImages]);
        e.target.value = '';
    };

    const removeImage = (index) => {
        if (isReadOnly) return;
        setImages(prev => prev.filter((_, i) => i !== index));
    };

    const getCanvasBase64 = (refObj) => {
        const canvas = refObj.current;
        if (!canvas) return '';
        const dpr = window.devicePixelRatio || 1;
        const ctx = canvas.getContext('2d');
        const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        // Check if canvas has meaningful content beyond the guide line
        let nonGuidePixels = 0;
        for (let i = 0; i < pixels.length; i += 4) {
            // Check for dark pixels (signature color) — not the guide line (#cbd5e1) or watermark
            if (pixels[i] < 100 && pixels[i + 3] > 50) {
                nonGuidePixels++;
                if (nonGuidePixels > 20) return canvas.toDataURL('image/png');
            }
        }
        return '';
    };

    const handleSaveAdmin = async () => {
        setLoading(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('place', place);
            formData.append('orderedBy', orderedBy);
            formData.append('workerName', workerName);
            formData.append('workDone', workDone);
            formData.append('date', date);


            const sig = getCanvasBase64(workerCanvasRef);
            if (sig) formData.append('signatureWorker', sig);

            images.forEach(img => {
                if (img.file) formData.append('images', img.file);
            });

            const res = await fetch(`${API_BASE}/api/jobApprovals`, {
                method: 'POST',
                body: formData
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error);

            setStatus(data.status);
            setSuccessMsg('טופס נשמר בהצלחה! קישור לחתימה נשלח למייל.');
            setGeneratedLink(`${window.location.origin}/job-approval/${data.token}`);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEmployeeSign = async () => {
        setLoading(true);
        setError('');
        try {
            const sig = getCanvasBase64(canvasRef);
            if (!sig) {
                throw new Error("חובה להוסיף חתימה לפני השליחה.");
            }

            const res = await fetch(`${API_BASE}/api/jobApprovals/${token}/sign`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ signatureOrderer: sig })
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error);

            setStatus('completed');
            setSignatureOrderer(data.signatureOrderer);
            setSuccessMsg('הטופס נחתם לאישור בהצלחה!');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading && !place && !error) {
        return <div className="text-center mt-20 text-xl font-bold">טוען טופס אישור... 🤔</div>;
    }

    if (error && !place) {
        return <div className="text-center mt-20 text-xl font-bold text-red-600">{error}</div>;
    }

    return (
        <div className="max-w-3xl mx-auto p-6 bg-gray-50 rounded-xl shadow-lg mt-10 print:shadow-none print:bg-white print:mt-0 print:p-0" dir="rtl">

            <Link to="/professionals" className="inline-flex items-center gap-2 text-gray-500 hover:text-blue-700 mb-4 transition-colors font-bold print:hidden">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                חזרה
            </Link>

            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-blue-900 print:text-black">טופס אישור עבודה</h2>
                <div className="flex gap-2 print:hidden">
                    <button
                        onClick={() => window.print()}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-bold transition-colors shadow-md"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        הדפס
                    </button>
                    {isNew && (
                        <button
                            onClick={handleSaveAdmin}
                            disabled={loading}
                            className={`flex items-center gap-2 px-5 py-2 rounded-lg font-bold transition-colors shadow-md text-white ${loading ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
                        >
                            {loading ? 'שומר...' : '💾 שמור טופס'}
                        </button>
                    )}
                    {status === 'pending' && token && (
                        <button
                            onClick={handleEmployeeSign}
                            disabled={loading}
                            className={`flex items-center gap-2 px-5 py-2 rounded-lg font-bold transition-colors shadow-md text-white ${loading ? 'bg-gray-400' : 'bg-purple-600 hover:bg-purple-700'}`}
                        >
                            {loading ? 'שומר חתימה...' : '✍️ חתום עכשיו'}
                        </button>
                    )}
                </div>
            </div>

            {successMsg && (
                <div className="mb-6 p-4 bg-green-100 text-green-800 font-bold rounded-lg border border-green-200">
                    {successMsg}
                </div>
            )}
            
            {error && (
                <div className="mb-6 p-4 bg-red-100 text-red-800 font-bold rounded-lg border border-red-200">
                    {error}
                </div>
            )}

            {generatedLink && (
                <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                    <p className="font-bold text-blue-900 mb-2">קישור לשליחה לעובד לחתימה:</p>
                    <div className="flex gap-2 mb-3">
                        <input type="text" readOnly value={generatedLink} className="flex-1 p-2 rounded border border-blue-300 bg-white" />
                        <button 
                            onClick={() => { navigator.clipboard.writeText(generatedLink); alert('הקישור הועתק לגזיר!'); }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 rounded font-bold"
                        >
                            העתק
                        </button>
                    </div>
                    <div className="flex gap-3">
                        <a 
                            href={`https://wa.me/?text=${encodeURIComponent('נא לחתום על טופס אישור העבודה בקישור הבא:\n' + generatedLink)}`}
                            target="_blank" rel="noreferrer"
                            className="flex-1 bg-green-500 hover:bg-green-600 text-white text-center py-2 rounded-lg font-bold flex items-center justify-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                            דווח בווטסאפ
                        </a>
                        <a 
                            href={`mailto:?subject=${encodeURIComponent('טופס לאישור עבודה')}&body=${encodeURIComponent('שלום, מצורף קישור לאישור עבודה:\n' + generatedLink)}`}
                            className="flex-1 bg-gray-600 hover:bg-gray-700 text-white text-center py-2 rounded-lg font-bold flex items-center justify-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                            שלח במייל
                        </a>
                    </div>
                </div>
            )}

            {isReadOnly && (
                <div className="mb-6 p-4 bg-yellow-50 text-yellow-800 font-bold rounded-lg border border-yellow-200 text-center text-lg">
                    🔒 טופס זה נחתם ואינו ניתן לעריכה.
                </div>
            )}

            {/* Print Header */}
            <div className="hidden print:block mb-6 pb-4" style={{ borderBottom: '3px solid #1e3a5f' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <img src="/assets/logo-circle.png" alt="Alum Nawatha" style={{ width: '70px', height: '70px', objectFit: 'contain' }} />
                        <div>
                            <h1 style={{ fontSize: '22pt', fontWeight: 'bold', color: '#1e3a5f', margin: 0, lineHeight: 1.2 }}>אלום נואטחה בע"מ</h1>
                            <p style={{ fontSize: '10pt', color: '#555', margin: '2px 0 0 0', letterSpacing: '2px' }}>ALUM NAWATHA LTD</p>
                            <p style={{ fontSize: '8pt', color: '#888', margin: '2px 0 0 0' }}>מערכות אלומיניום מתקדמות | טל: 052-878-4031</p>
                        </div>
                    </div>
                </div>
                <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '14pt', fontWeight: 'bold', color: '#1e3a5f' }}>
                    טופס אישור ביצוע עבודה
                </div>
            </div>

            {/* Screen Logo Header */}
            <div className="flex items-center gap-4 mb-6 border-b pb-4 print:hidden">
                <img src="/assets/logo-circle.png" alt="Alum Nawatha" className="w-14 h-14 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                <div>
                    <div className="text-lg font-bold text-blue-900">אלום נואטחה בע"מ</div>
                    <div className="text-xs text-gray-500">טופס אישור ביצוע עבודה / ארכיון</div>
                </div>
            </div>

            {/* Form Fields */}
            <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                        <label className="block text-gray-700 font-bold mb-2">תאריך:</label>
                        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} disabled={!isNew} className="border-2 border-blue-200 p-3 rounded-lg w-full outline-none print:border print:border-gray-300 print:p-2 disabled:bg-gray-100 disabled:cursor-not-allowed" />
                    </div>
                    <div>
                        <label className="block text-gray-700 font-bold mb-2">מקום / אתר:</label>
                        <input type="text" value={place} onChange={(e) => setPlace(e.target.value)} disabled={!isNew} className="border-2 border-blue-200 p-3 rounded-lg w-full outline-none print:border print:border-gray-300 print:p-2 disabled:bg-gray-100 disabled:cursor-not-allowed" placeholder="כתובת או שם האתר" />
                    </div>
                </div>

                <div>
                    <label className="block text-gray-700 font-bold mb-2">שם מזמין העבודה:</label>
                    <input type="text" value={orderedBy} onChange={(e) => setOrderedBy(e.target.value)} disabled={!isNew} className="border-2 border-blue-200 p-3 rounded-lg w-full outline-none print:border print:border-gray-300 print:p-2 disabled:bg-gray-100 disabled:cursor-not-allowed" placeholder="שם מלא של מזמין העבודה" />
                </div>

                <div>
                    <label className="block text-gray-700 font-bold mb-2">תיאור העבודה שבוצעה:</label>
                    <textarea value={workDone} onChange={(e) => setWorkDone(e.target.value)} disabled={!isNew} rows={5} className="border-2 border-blue-200 p-3 rounded-lg w-full outline-none resize-y print:border print:border-gray-300 print:p-2 disabled:bg-gray-100 disabled:cursor-not-allowed" placeholder="פירוט העבודות שבוצעו באתר..." />
                </div>

                {/* Worker Info */}
                <div>
                    <label className="block text-gray-700 font-bold mb-2">שם העובד / מבצע העבודה:</label>
                    <input type="text" value={workerName} onChange={(e) => setWorkerName(e.target.value)} disabled={!isNew} className="border-2 border-blue-200 p-3 rounded-lg w-full outline-none print:border print:border-gray-300 print:p-2 disabled:bg-gray-100 disabled:cursor-not-allowed" placeholder="שם מלא של מבצע העבודה" />
                </div>

                {/* Image Upload Section */}
                <div>
                    <label className="block text-gray-700 font-bold mb-2">תמונות מהאתר (עד 3):</label>

                    {/* Hidden file input */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        multiple
                        onChange={handleImageAdd}
                        className="hidden"
                    />

                    {/* Image grid */}
                    <div className="grid grid-cols-3 gap-3 mb-3">
                        {images.map((imgObj, i) => (
                            <div key={i} className="relative group">
                                <img
                                    src={imgObj.preview || imgObj.url}
                                    alt={`תמונה ${i + 1}`}
                                    className="w-full h-32 object-cover rounded-lg border-2 border-gray-200 print:h-40 print:border print:border-gray-300"
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                                {!isReadOnly && isNew && (
                                    <button
                                        onClick={() => removeImage(i)}
                                        className="print:hidden absolute top-1 left-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        ))}

                        {/* Add button placeholder */}
                        {images.length < 3 && isNew && (
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="print:hidden w-full h-32 border-2 border-dashed border-blue-300 rounded-lg flex flex-col items-center justify-center text-blue-400 hover:text-blue-600 hover:border-blue-500 transition-colors cursor-pointer bg-white"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="text-xs font-bold">צלם / העלה</span>
                            </button>
                        )}
                    </div>
                    {isNew && <p className="text-xs text-gray-400 print:hidden">{images.length}/3 תמונות</p>}
                </div>

                {/* Signatures Side by Side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Orderer Signature */}
                    <div>
                        <label className="block text-gray-700 font-bold mb-2">חתימת המזמין (לקוח אחראי):</label>
                        <div className={`border-2 rounded-xl overflow-hidden bg-white print:border print:border-gray-300 transition-all duration-300 ${isNew || isReadOnly ? 'border-gray-200 bg-gray-50 opacity-80 pointer-events-none' : 'border-blue-300 shadow-sm hover:shadow-md hover:border-blue-400'}`}>
                            {signatureOrderer && (isNew || isReadOnly) ? (
                                <img src={signatureOrderer} alt="חתימה" className="w-full h-[150px] object-contain" />
                            ) : (
                                <canvas
                                    ref={canvasRef}
                                    className="w-full cursor-crosshair touch-none"
                                    style={{ height: '150px', display: 'block' }}
                                    onMouseDown={startDraw(canvasRef)}
                                    onTouchStart={startDraw(canvasRef)}
                                />
                            )}
                        </div>
                        {(!isNew && !isReadOnly) && (
                            <button onClick={clearCanvas(canvasRef)} className="flex items-center gap-1 text-sm text-red-500 hover:text-red-700 font-bold mt-2 print:hidden transition-colors">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                נקה חתימה
                            </button>
                        )}
                        {isReadOnly && <div className="text-sm text-green-600 font-bold mt-1">✓ נחתם</div>}
                    </div>

                    {/* Worker Signature */}
                    <div>
                        <label className="block text-gray-700 font-bold mb-2">חתימת מבצע העבודה (אלום נואטחה):</label>
                        <div className={`border-2 rounded-xl overflow-hidden bg-white print:border print:border-gray-300 transition-all duration-300 ${!isNew ? 'border-gray-200 bg-gray-50 opacity-80 pointer-events-none' : 'border-blue-300 shadow-sm hover:shadow-md hover:border-blue-400'}`}>
                            {signatureWorker && !isNew ? (
                                <img src={signatureWorker} alt="חתימה" className="w-full h-[150px] object-contain" />
                            ) : (
                                <canvas
                                    ref={workerCanvasRef}
                                    className="w-full cursor-crosshair touch-none"
                                    style={{ height: '150px', display: 'block' }}
                                    onMouseDown={startDraw(workerCanvasRef)}
                                    onTouchStart={startDraw(workerCanvasRef)}
                                />
                            )}
                        </div>
                        {isNew && (
                            <button onClick={clearCanvas(workerCanvasRef)} className="flex items-center gap-1 text-sm text-red-500 hover:text-red-700 font-bold mt-2 print:hidden transition-colors">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                נקה חתימה
                            </button>
                        )}
                    </div>
                </div>
            </div>

                {/* Bottom Save & Send Button — visible when orderer is signing */}
                {status === 'pending' && token && (
                    <div className="mt-8 print:hidden">
                        <button
                            onClick={handleEmployeeSign}
                            disabled={loading}
                            className={`w-full py-5 rounded-2xl font-black text-lg tracking-wide transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-3 text-white ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-l from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600'}`}
                            style={!loading ? { boxShadow: '0 8px 30px rgba(16, 185, 129, 0.35)' } : {}}
                        >
                            {loading ? (
                                <span className="inline-flex items-center gap-2">
                                    <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                    </svg>
                                    שומר ושולח...
                                </span>
                            ) : (
                                <>
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    שמור ושלח
                                </>
                            )}
                        </button>
                    </div>
                )}
        </div>
    );
}
